import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { OSK_ICON_SIZE_BY_CONTROL_SIZE } from '../../core/utils/osk-size.utils';
import { OSKColorVariant, OSKIconName, OSKSize } from '../../types/osk-theme.types';
import { OSKIconComponent } from '../osk-icon/osk-icon.component';

export type OSKButtonAppearance = 'solid' | 'outline' | 'soft' | 'ghost' | 'gradient';

export type OSKButtonShape = 'rounded' | 'circle' | 'pill';

/**
 * Atom: <osk-button>
 * Reusable standalone button built with Angular Signals supporting 9 color variants, 4 appearances, and loading state.
 */
@Component({
  selector: 'osk-button',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-button.component.scss',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property -- Angular now recommends host metadata over @HostBinding; angular-eslint 18 removed this rule.
  host: {
    '[attr.aria-label]': 'null',
    '(click)': 'onHostClick($event)'
  },
  template: `
    <ng-template #btnContent>
      @if (loading()) {
        <svg
          class="osk-btn__spinner"
          [attr.width]="iconSize()"
          [attr.height]="iconSize()"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-opacity="0.25"
            stroke-width="2.5"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
      } @else if (leadingIcon()) {
        <osk-icon [name]="leadingIcon()!" [size]="iconSize()"></osk-icon>
      }
      <ng-content></ng-content>
      @if (trailingIcon() && !loading()) {
        <osk-icon [name]="trailingIcon()!" [size]="iconSize()"></osk-icon>
      }
    </ng-template>

    @if (resolvedLink(); as targetLink) {
      <a
        [routerLink]="isDisabled() ? null : targetLink"
        [attr.aria-busy]="loading()"
        [attr.aria-disabled]="isDisabled() || null"
        [attr.tabindex]="isDisabled() ? -1 : null"
        [attr.aria-label]="resolvedAriaLabel()"
        [class]="buttonClasses()"
        (click)="handleClick($event)"
      >
        <ng-container [ngTemplateOutlet]="btnContent" />
      </a>
    } @else {
      <button
        [attr.type]="type()"
        [disabled]="isDisabled()"
        [attr.aria-busy]="loading()"
        [attr.aria-label]="resolvedAriaLabel()"
        [class]="buttonClasses()"
        (click)="handleClick($event)"
      >
        <ng-container [ngTemplateOutlet]="btnContent" />
      </button>
    }
  `
})
export class OSKButtonComponent {
  readonly color = input<OSKColorVariant>('primary');
  readonly appearance = input<OSKButtonAppearance>('solid');
  readonly size = input<OSKSize>('md');
  readonly shape = input<OSKButtonShape>('rounded');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  readonly iconOnly = input(false, { transform: booleanAttribute });
  readonly fullWidth = input(false, { transform: booleanAttribute });
  readonly leadingIcon = input<OSKIconName | undefined>(undefined);
  readonly trailingIcon = input<OSKIconName | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly ariaLabelAttr = input<string | undefined>(undefined, { alias: 'aria-label' });
  /** Optional internal router navigation target; renders an `<a>` instead of `<button>`. */
  readonly link = input<string | readonly unknown[] | null | undefined>(undefined);
  /** Alias for `link` when `RouterLink` is not imported on the host component. */
  readonly routerLink = input<string | readonly unknown[] | null | undefined>(undefined);

  readonly clicked = output<MouseEvent>();

  protected readonly isDisabled = computed<boolean>(() => this.disabled() || this.loading());
  protected readonly resolvedAriaLabel = computed<string | null>(
    () => this.ariaLabel() || this.ariaLabelAttr() || null
  );
  protected readonly resolvedLink = computed<string | unknown[] | null | undefined>(() => {
    const raw = this.link() ?? this.routerLink();
    return Array.isArray(raw) ? [...raw] : (raw as string | null | undefined);
  });

  protected readonly buttonClasses = computed<string>(() => {
    const classes: string[] = [
      'osk-btn',
      `osk-btn--${this.color()}`,
      `osk-btn--${this.appearance()}`,
      `osk-btn--${this.size()}`
    ];
    if (this.iconOnly()) {
      classes.push('osk-btn--icon-only');
    }
    if (this.shape() !== 'rounded') {
      classes.push(`osk-btn--${this.shape()}`);
    }
    if (this.fullWidth()) {
      classes.push('osk-btn--block');
    }
    if (this.loading()) {
      classes.push('osk-btn--loading');
    }
    if (this.isDisabled() && this.resolvedLink()) {
      classes.push('osk-btn--disabled');
    }
    return classes.join(' ');
  });

  protected readonly iconSize = computed<number>(() => OSK_ICON_SIZE_BY_CONTROL_SIZE[this.size()]);

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    this.hostRef.nativeElement.focus = (options?: FocusOptions): void => {
      this.hostRef.nativeElement.querySelector<HTMLElement>('.osk-btn')?.focus(options);
    };
  }

  onHostClick(event: MouseEvent): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  handleClick(event: MouseEvent): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    this.clicked.emit(event);
  }
}
